import { useMemo } from 'react';
import { AlertTriangle, Zap, FileWarning, Clock, TrendingUp } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface Insight {
  type: 'error' | 'warning' | 'info';
  icon: React.ReactNode;
  title: string;
  description: string;
  entries?: HAREntry[];
}

export function PerformanceInsights() {
  const { filteredEntries, stats, selectEntry } = useHARStore();

  const insights = useMemo(() => {
    if (!stats || filteredEntries.length === 0) return [];

    const result: Insight[] = [];

    // Slow requests (> 1s)
    const slowRequests = filteredEntries.filter(e => e.time > 1000);
    if (slowRequests.length > 0) {
      const verySlowCount = slowRequests.filter(e => e.time > 3000).length;
      result.push({
        type: verySlowCount > 0 ? 'error' : 'warning',
        icon: <Clock className="w-4 h-4" />,
        title: `${slowRequests.length} slow request${slowRequests.length > 1 ? 's' : ''} detected`,
        description: verySlowCount > 0
          ? `${verySlowCount} requests took over 3 seconds. Consider optimizing server response times or implementing caching.`
          : 'Requests taking over 1 second may impact user experience.',
        entries: slowRequests.sort((a, b) => b.time - a.time).slice(0, 5),
      });
    }

    // Large payloads (> 100KB)
    const largePayloads = filteredEntries.filter(e => (e.response.content.size || 0) > 100 * 1024);
    if (largePayloads.length > 0) {
      const veryLargeCount = largePayloads.filter(e => (e.response.content.size || 0) > 500 * 1024).length;
      result.push({
        type: veryLargeCount > 0 ? 'warning' : 'info',
        icon: <FileWarning className="w-4 h-4" />,
        title: `${largePayloads.length} large payload${largePayloads.length > 1 ? 's' : ''} detected`,
        description: veryLargeCount > 0
          ? `${veryLargeCount} responses exceed 500KB. Consider compression, lazy loading, or splitting resources.`
          : 'Some responses are over 100KB. Consider optimizing asset sizes.',
        entries: largePayloads.sort((a, b) => (b.response.content.size || 0) - (a.response.content.size || 0)).slice(0, 5),
      });
    }

    // HTTP errors (4xx, 5xx)
    const errorRequests = filteredEntries.filter(e => e.response.status >= 400);
    if (errorRequests.length > 0) {
      const serverErrors = errorRequests.filter(e => e.response.status >= 500).length;
      result.push({
        type: serverErrors > 0 ? 'error' : 'warning',
        icon: <AlertTriangle className="w-4 h-4" />,
        title: `${errorRequests.length} failed request${errorRequests.length > 1 ? 's' : ''}`,
        description: serverErrors > 0
          ? `${serverErrors} server errors (5xx) detected. Check server logs for issues.`
          : 'Client errors (4xx) may indicate missing resources or authorization issues.',
        entries: errorRequests.slice(0, 5),
      });
    }

    // Redirect chains
    const redirects = filteredEntries.filter(e => e.response.status >= 300 && e.response.status < 400);
    if (redirects.length > 3) {
      result.push({
        type: 'info',
        icon: <TrendingUp className="w-4 h-4" />,
        title: `${redirects.length} redirects detected`,
        description: 'Multiple redirects can slow page load. Consider reducing redirect chains.',
        entries: redirects.slice(0, 5),
      });
    }

    // Uncompressed responses
    const uncompressedLarge = filteredEntries.filter(e => {
      const size = e.response.content.size || 0;
      const hasCompression = e.response.headers.some(
        h => h.name.toLowerCase() === 'content-encoding'
      );
      const isCompressible = ['text/', 'application/json', 'application/javascript'].some(
        t => e.response.content.mimeType?.includes(t)
      );
      return size > 10 * 1024 && !hasCompression && isCompressible;
    });
    if (uncompressedLarge.length > 0) {
      result.push({
        type: 'info',
        icon: <Zap className="w-4 h-4" />,
        title: `${uncompressedLarge.length} uncompressed response${uncompressedLarge.length > 1 ? 's' : ''}`,
        description: 'Enable gzip/brotli compression for text-based resources to reduce transfer size.',
        entries: uncompressedLarge.slice(0, 5),
      });
    }

    return result;
  }, [filteredEntries, stats]);

  if (insights.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center mx-auto mb-3">
          <Zap className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No issues detected</h3>
        <p className="text-xs text-slate-500">All requests appear to be performing well.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div
              key={index}
              className={cn(
                'p-3 rounded-lg border',
                insight.type === 'error' && 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30',
                insight.type === 'warning' && 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30',
                insight.type === 'info' && 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'p-1.5 rounded',
                    insight.type === 'error' && 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400',
                    insight.type === 'warning' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                    insight.type === 'info' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                  )}
                >
                  {insight.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className={cn(
                      'text-sm font-medium',
                      insight.type === 'error' && 'text-red-700 dark:text-red-400',
                      insight.type === 'warning' && 'text-yellow-700 dark:text-yellow-400',
                      insight.type === 'info' && 'text-blue-700 dark:text-blue-400'
                    )}
                  >
                    {insight.title}
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {insight.description}
                  </p>

                  {insight.entries && insight.entries.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {insight.entries.map((entry, i) => (
                        <button
                          key={entry._id || i}
                          onClick={() => selectEntry(entry)}
                          className="w-full text-left text-xs p-1.5 rounded bg-white/50 dark:bg-slate-900/30 hover:bg-white dark:hover:bg-slate-900/50 transition-colors flex items-center justify-between gap-2"
                        >
                          <span className="truncate text-slate-700 dark:text-slate-300">
                            {getFileName(entry.request.url)}
                          </span>
                          <span className="flex-shrink-0 text-slate-500">
                            {entry.time > 1000
                              ? formatTime(entry.time)
                              : formatBytes(entry.response.content.size || 0)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
